import {
    useScopedMutation,
    useScopedQuery,
    useScopedQueryClient,
} from '@/lib/scopedQuery';
import { api } from './client';

export interface ServiceCode {
    id: string;
    code: string;
    desc: string;
    status: 'Active' | 'Inactive';
}

export interface ServiceCodeWritePayload {
    code?: string;
    desc?: string;
    status?: 'Active' | 'Inactive';
}

function normalizeStatus(s: string): 'Active' | 'Inactive' {
    return String(s).toLowerCase() === 'active' ? 'Active' : 'Inactive';
}

function normalize(row: { id: string; code: string; desc: string; status: string }): ServiceCode {
    return { id: row.id, code: row.code, desc: row.desc || '', status: normalizeStatus(row.status) };
}

async function fetchServiceCodes(): Promise<ServiceCode[]> {
    const res = await api.get<Array<{ id: string; code: string; desc: string; status: string }>>('/service-codes');
    return res.data.map(normalize);
}

export function useServiceCodes() {
    return useScopedQuery({
        queryKey: ['service-codes'] as const,
        queryFn: fetchServiceCodes,
        staleTime: 60_000,
    });
}

export function useCreateServiceCode() {
    const qc = useScopedQueryClient();
    return useScopedMutation({
        mutationFn: async (payload: ServiceCodeWritePayload) => {
            const res = await api.post('/service-codes', payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['service-codes'] }),
    });
}

export function useUpdateServiceCode() {
    const qc = useScopedQueryClient();
    return useScopedMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ServiceCodeWritePayload }) => {
            const res = await api.patch(`/service-codes/${id}`, payload);
            return normalize(res.data);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['service-codes'] }),
    });
}

export function useDeleteServiceCode() {
    const qc = useScopedQueryClient();
    return useScopedMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/service-codes/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['service-codes'] }),
    });
}
